import { FundraisingLanguages } from 'src/constants/FundraisingLanguages';
import { FundraisingTypes } from 'src/constants/FundraisingTypes';
import { Moment } from 'moment';

export type FundraisingTypeList =
  FundraisingInfo | FundraisingMatchingInfo | FundraisingProductsInfo | FundraisingAmbassadors
  | FundraisingAssets | FundraisingNotifications;

export const enum TABS {
  information = 'information',
  products = 'products',
  notifications = 'notifications',
  assets = 'assets',
  ambassador = 'ambassador',
}

export const enum FundraisingState {
  draft = 'draft',
  final = 'final',
}

export interface FundraisingInfo {
  account_id: number;
  active: boolean;
  admin_fee: string;
  ask_for_fee: boolean;
  bank_account_id: number;
  countdown_end_date: Moment;
  department_id: number;
  default_language: FundraisingLanguages;
  end_date: Moment;
  group_ids: number[];
  has_end: boolean;
  id?: number;
  allow_installment: boolean | undefined;
  max_amount: number | string;
  max_installment: number | string;
  name: string;
  payment_gateway_id: number;
  program_id: number;
  show_countdown: boolean;
  show_suggested: boolean;
  slug: string;
  start_date: Moment;
  state: FundraisingState;
  type: FundraisingTypes;
}

export interface FundraisingMatchingInfo {
  active_matching: boolean,
  bonus_round_active: boolean,
  bonus_round_goal: number | string,
  end_amount_multiplier: number | string,
  goal: number | string,
  matching_multiplier: number | string,
  show_goal: boolean,
  show_raised_amount: boolean,
  start_amount_multiplier: number | string,
}

export interface FundraisingProductsInfo {
  all_products_visible: boolean,
  show_in_dropdown: boolean,
  show_suggested: boolean,
  fixed_price_as_checkbox: boolean,
  fixed_price_allow_quantity: boolean,
  product_fee_id: number | string,
  product_suggested_id: number | string,
  products: number[],
}

export interface FundraisingNotifications {
  contacts: number[],
  send_thanks_email: boolean,
  thanks_email_body?: string,
  send_thanks_sms: boolean,
  thanks_sms_text?: string,
  send_email_notifications: boolean,
  send_sms_notifications: boolean,
  fb_title: string,
  fb_description: string,
}

export interface Ambassador {
  id?: string | number;
  contact_id: number | string;
  fundraising_id: number | string,
  notify_by_email: boolean,
  notify_by_sms: boolean,
  invitation_sms_sent: boolean,
  invitation_email_sent: boolean,
  avatar?: string,
  name: string,
  goal: number | string,
  slug: string,
  message: string,
  video_link: string,
}

export interface AmbassadorPayload {
  id?: string | number;
  event_id: number | string,
  contact_id: number | string;
  notify_by_email: boolean,
  notify_by_sms: boolean,
  invitation_sms_sent: boolean,
  invitation_email_sent: boolean,
  avatar?: string,
  name: string,
  goal: number | string,
  slug: string,
  message: string,
  video_link: string,
}

export interface FundraisingAmbassadors {
  message_sms: string,
  message_email: string,
  show_ambassador_section: boolean,
  show_ambassador_goal: boolean,
  show_ambassador_real_amount: boolean,
  show_donors_section: boolean,
  show_donor_real_amount: boolean,
  show_donor_amount: boolean,
  show_donors_comments: boolean,
}

export type Devices = 'desktop' | 'mobile';

export interface MediaAsset {
  video_url: Nullable<string>;
  image_url: string;
}

export interface MainAsset {
  desktop: MediaAsset[];
  mobile: MediaAsset[];
}

export interface SecondaryAsset {
  desktop: string[];
  mobile: string[];
}

export type MainAssets = {
  [key in FundraisingLanguages]?: MainAsset;
};

export type SecondaryAssets = {
  [key in FundraisingLanguages]?: string[];
};

export type CampaignMessage = {
  [key in FundraisingLanguages]: string;
};

export interface FundraisingAssets {
  theme_color: string,
  show_main_slider: boolean,
  main_slider: MainAssets,
  show_secondary_slider: boolean,
  secondary_slider: SecondaryAssets,
  show_matcher_slider: boolean,
  matcher_slider: string[],
  show_sponsor_slider: boolean,
  sponsor_slider: string[],
  campaign_message: CampaignMessage,
  sections: string[],
}

export interface FundraisingModel {
  fundraisingInfo: Nullable<FundraisingInfo>,
  matchingInfo: Nullable<FundraisingMatchingInfo>,
  products: Nullable<FundraisingProductsInfo>,
  ambassadors: Nullable<FundraisingAmbassadors>,
  notifications: Nullable<FundraisingNotifications>,
  assets: Nullable<FundraisingAssets>,
}

export interface FundraisingPayload {
  id?: number;
  type: FundraisingTypes;
  name: string;
  slug: string;
  active: boolean;
  default_language: FundraisingLanguages;
  start_date: Moment;
  end_date: Moment;
  has_end: boolean;
  show_countdown: boolean;
  countdown_end_date: Moment;
  ask_for_fee: boolean;
  admin_fee: string;
  show_goal: boolean;
  show_suggested: boolean;
  max_amount: number | string;
  program_id: number;
  department_id: number;
  account_id: number;
  bank_account_id: number;
  payment_gateway_id: number;
  allow_installment: boolean | undefined;
  max_installment: number | string;
  group_ids: number[];
  matching_info: Nullable<FundraisingMatchingInfo>,

  // From FundraisingProductsInfo
  all_products_visible: boolean;
  show_in_dropdown: boolean;
  fixed_price_as_checkbox: boolean;
  fixed_price_allow_quantity: boolean;
  product_fee_id: number | string;
  product_suggested_id: number | string;
  products: number[];

  // From FundraisingAmbassadors
  message_sms: string;
  message_email: string;
  show_ambassador_section: boolean;
  show_ambassador_goal: boolean;
  show_ambassador_real_amount: boolean;
  show_donors_section: boolean;
  show_donor_real_amount: boolean;
  show_donor_amount: boolean;
  show_donors_comments: boolean;

  // From FundraisingNotifications
  contacts: number[];
  send_thanks_email: boolean;
  thanks_email_body?: string;
  send_thanks_sms: boolean;
  thanks_sms_text?: string;
  send_email_notifications: boolean;
  send_sms_notifications: boolean;
  fb_title: string;
  fb_description: string;

  // From FundraisingAssets (optional: can be full nested object or string)
  theme_color: string,
  show_main_slider: boolean,
  main_slider: MainAssets,
  show_secondary_slider: boolean,
  secondary_slider: SecondaryAssets,
  show_matcher_slider: boolean,
  matcher_slider: string[],
  show_sponsor_slider: boolean,
  sponsor_slider: string[],
  campaign_message: CampaignMessage,
  sections: string[],
}

export type FundraisingModelList = {
  [id:string]: FundraisingModel;
};

export enum Step {
  info = 1,
  products = 2,
  ambassadors = 3,
  notifications = 4,
  assets = 5,
}

export interface FundraisingTransaction {
  id: number,
  invoice_id: number
  payment_id: number,
  ambassador_id: string,
  contact_id: number,
  title: string,
  is_anonymous: boolean,
  amount_multiplier: number | string,
  donor_name_displayed: string,
  donor_amount_paid: string,
  donor_currency_id: number | string,
  donor_convertion_rate: number,
  nbr_installment: number,
  installments_organisation: number,
  installments_donor: number,
  donor_comment: number,
  show_pledge: boolean,
  source_reference: string
  source_reference_url: string
  pledge_date: Moment,
  created_at: Moment,
  updated_at: Moment,
  deleted_at: Moment,
}

export type FundraisingTransactionsModelList = {
  [id:string]: FundraisingTransaction;
};
